import React, { useMemo, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../utils/config';
import { saveAs } from 'file-saver';

const REPORTS = [
  { group: 'Sales', items: [
    { key: 'sales.customer', label: 'Sales - Customer wise' },
    { key: 'sales.product',  label: 'Sales - Product wise' },
    { key: 'sales.locality', label: 'Sales - Locality wise' },
  ]},
  { group: 'Returns', items: [
    { key: 'returns.customer', label: 'Sales Return - Customer wise' },
    { key: 'returns.product',  label: 'Sales Return - Product wise' },
    { key: 'returns.locality', label: 'Sales Return - Locality wise' },
  ]},
  { group: 'Products', items: [
    { key: 'products.priceList',  label: 'Price List' },
    { key: 'batches.expired',     label: 'Expired Products' },
    { key: 'batches.expiring',    label: 'Expiring (< 91 days)' },
    { key: 'products.notRunning', label: 'Not Running (no sale in 60 days)' },
    { key: 'products.byGst',      label: 'Product list - GST wise' },
    { key: 'products.byCategory', label: 'Product list - Category wise' },
    { key: 'products.byBatch',    label: 'Product list - Batch wise' },
  ]},
  { group: 'Customers', items: [
    { key: 'customers.inactive', label: 'Not active (no order in last 60 days)' },
    { key: 'customers.notPaid',  label: 'Customer not paid (buckets)' },
    { key: 'customers.outstanding',  label: 'Customer Outstanding' },

  ]},
  { group: 'Payments', items: [
    { key: 'payments.locality', label: 'Payment received - Locality wise' },
    { key: 'payments.customer', label: 'Payment received - Customer wise' },
    { key: 'payments.mode',     label: 'Payment received - Mode wise' },
  ]},
  { group: 'Profit', items: [
    { key: 'profit.byCustomer', label: 'Profit by Customer' },
    { key: 'profit.byInvoice',  label: 'Profit by Invoice' },
  ]},
  { group: 'Expenses', items: [
    { key: 'expenses.byDate',    label: 'Expenses - Date wise' },
    { key: 'expenses.byCustomer',label: 'Expenses - Customer wise' },
  ]},
];

export default function Reports() {
  const [reportKey, setReportKey] = useState('sales.customer');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [locality, setLocality] = useState('');
  const [days, setDays] = useState(60);
  const [horizon, setHorizon] = useState(90);
  const [bucket, setBucket] = useState('60'); // for notPaid
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  const needsLocality = useMemo(() => ['sales.locality','returns.locality','payments.locality'].includes(reportKey), [reportKey]);
  const needsDays = useMemo(() => ['products.notRunning','customers.inactive'].includes(reportKey), [reportKey]);
  const needsHorizon = useMemo(() => reportKey === 'batches.expiring', [reportKey]);
  const needsBucket = useMemo(() => reportKey === 'customers.notPaid', [reportKey]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/reportmodule/run`, {
        reportKey,
        filters: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          locality: locality || undefined,
          days,
          horizon,
          bucket,
        },
      });
      setColumns(data.columns || []);
      setRows(data.rows || []);
    } catch (e) {
      console.error(e);
      setColumns([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!rows.length) return;
    const header = columns.join(',');
    const body = rows.map(r =>
      columns.map(c => {
        const v = r[c];
        if (v == null) return '';
        const s = String(v).replace(/"/g,'""');
        return `"${s}"`;
      }).join(',')
    ).join('\n');
    const csv = '\uFEFF' + header + '\n' + body; // BOM for Excel
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${reportKey}-${Date.now()}.csv`);
  };

  return (
    <div className="p-4 md:p-6 mx-auto max-w-7xl space-y-4">
      <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Reports</h1>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-white p-4 rounded-xl shadow">
        <div className="sm:col-span-2 lg:col-span-2">
          <label className="text-sm text-slate-600">Report</label>
          <select
            className="mt-1 w-full border rounded-xl px-3 py-3 text-sm"
            value={reportKey}
            onChange={e=>setReportKey(e.target.value)}
          >
            {REPORTS.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.items.map(it => <option key={it.key} value={it.key}>{it.label}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm text-slate-600">From</label>
          <input
            type="date"
            className="mt-1 w-full border rounded-xl px-3 py-3 text-sm"
            value={dateFrom}
            onChange={e=>setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm text-slate-600">To</label>
          <input
            type="date"
            className="mt-1 w-full border rounded-xl px-3 py-3 text-sm"
            value={dateTo}
            onChange={e=>setDateTo(e.target.value)}
          />
        </div>

        {needsLocality && (
          <div>
            <label className="text-sm text-slate-600">Locality</label>
            <input
              className="mt-1 w-full border rounded-xl px-3 py-3 text-sm"
              value={locality}
              onChange={e=>setLocality(e.target.value)}
              placeholder="e.g., Kishangarh"
            />
          </div>
        )}

        {needsDays && (
          <div>
            <label className="text-sm text-slate-600">Days</label>
            <input
              type="number"
              className="mt-1 w-full border rounded-xl px-3 py-3 text-sm"
              value={days}
              onChange={e=>setDays(Number(e.target.value || 0))}
            />
          </div>
        )}

        {needsHorizon && (
          <div>
            <label className="text-sm text-slate-600">Horizon (days)</label>
            <input
              type="number"
              className="mt-1 w-full border rounded-xl px-3 py-3 text-sm"
              value={horizon}
              onChange={e=>setHorizon(Number(e.target.value || 0))}
            />
          </div>
        )}

        {needsBucket && (
          <div>
            <label className="text-sm text-slate-600">Bucket</label>
            <select
              className="mt-1 w-full border rounded-xl px-3 py-3 text-sm"
              value={bucket}
              onChange={e=>setBucket(e.target.value)}
            >
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 180 days</option>
              <option value="FY">Not in this FY</option>
              <option value="NEVER">Never paid once</option>
            </select>
          </div>
        )}

        <div className="sm:col-span-2 lg:col-span-4 flex flex-col sm:flex-row gap-2">
          <button
            onClick={run}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-3 text-sm font-medium disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Running…' : 'Run'}
          </button>
          <button
            onClick={downloadCSV}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm bg-white"
          >
            Download CSV
          </button>
        </div>
      </div>

      {/* Sticky mobile action bar (optional) */}
      <div className="sticky bottom-3 z-10 flex gap-2 sm:hidden">
        <button
          onClick={run}
          className="flex-1 rounded-xl bg-blue-600 text-white px-4 py-3 text-sm"
          disabled={loading}
        >
          {loading ? 'Running…' : 'Run'}
        </button>
        <button
          onClick={downloadCSV}
          className="flex-1 rounded-xl border px-4 py-3 text-sm bg-white"
        >
          CSV
        </button>
      </div>

      {/* Results: table on md+, cards on mobile */}
      {/* Desktop/tablet */}
      <div className="hidden md:block bg-white rounded-xl shadow overflow-x-auto">
        {!rows.length ? (
          <div className="p-6 text-slate-500 text-sm">No data</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-slate-700">
                {columns.map(c => (
                  <th key={c} className="px-3 py-2 text-left whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t">
                  {columns.map(c => (
                    <td key={c} className="px-3 py-2 whitespace-nowrap">
                      {String(r[c] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {!rows.length ? (
          <div className="p-4 text-slate-500 text-sm">No data</div>
        ) : (
          rows.map((r, i) => (
            <div key={i} className="bg-white rounded-xl shadow p-3">
              {columns.map((c) => (
                <div key={c} className="flex justify-between gap-3 py-1">
                  <span className="text-slate-500 text-xs uppercase">{c}</span>
                  <span className="text-slate-800 text-sm font-medium text-right break-all">
                    {String(r[c] ?? '')}
                  </span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
